import { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { OBJECT_TYPE } from "common/constants";
import {
  CurrentUserQuery,
  useCreateWorkflowMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { Button } from "../../Common/Buttons/Button";
import { CommonModal } from "../../Common/CommonModal";
import Modal from "../../Common/Modal";
import { Body } from "../../Common/Typography";
import { SelectionCard } from "../../Inputs/SelectionCard";

type WorkflowDefinition = NonNullable<
  CurrentUserQuery["currentUser"]
>["account"]["workflowDefinitions"][number];

type CreateWorkflowButtonProps = {
  objectId: string;
  objectType: OBJECT_TYPE;
  customMapId?: string;
  objectDisplay?: string;
  onSuccess: () => void;
};

export const CreateWorkflowButton = ({
  objectId,
  objectType,
  customMapId,
  objectDisplay,
  onSuccess,
}: CreateWorkflowButtonProps) => {
  const { user, admin, account } = useContext(AuthContext);
  const { addErrorToast, addSuccessToast } = useStatusToasts();

  const workflowDefinitions = (account?.workflowDefinitions ?? []).filter(def =>
    def.initialSubmissionType.attachments.some(attachment => {
      if (objectType === OBJECT_TYPE.PROPERTY) {
        return attachment.type === OBJECT_TYPE.PROPERTY;
      } else {
        return (
          attachment.type === objectType &&
          attachment.customMapIds?.some(id => id === customMapId)
        );
      }
    }),
  );

  const [createWorkflowMutation, { loading }] = useCreateWorkflowMutation();

  const handleCreate = async (workflowDefinitionId: string) => {
    await createWorkflowMutation({
      variables: {
        data: {
          workflowDefinitionId,
          objectId,
          objectType,
        },
      },
      onCompleted: data => {
        onSuccess();
        addSuccessToast("Workflow created successfully");
        track("Workflow created", {
          workflowId: data.createWorkflow.id,
          workflowDefinitionId,
          objectId,
          objectType,
          userId: user?.id ?? admin?.id,
          userEmail: user?.email ?? admin?.email,
        });
      },
      onError: () => {
        addErrorToast(
          `There was an issue creating your workflow. Please try again or contact support@withforerunner.com`,
        );
      },
    });
  };

  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <SelectWorkflowDefinition
          workflowDefinitions={workflowDefinitions}
          objectDisplay={objectDisplay}
          onCancel={hideModal}
          onSelect={async (workflowDefinitionId: string) => {
            await handleCreate(workflowDefinitionId);
            hideModal();
          }}
        />
      </Modal>
    ),
    [workflowDefinitions, objectDisplay, handleCreate],
  );

  if (workflowDefinitions.length === 0) {
    return null;
  }

  return (
    <Button
      iconStart="plus"
      size="small"
      variant="secondary"
      title="Add new"
      isPending={loading}
      isDisabled={workflowDefinitions.length === 0}
      onClick={
        workflowDefinitions.length === 1
          ? () => void handleCreate(workflowDefinitions[0]!.id)
          : showModal
      }
    />
  );
};

type SelectWorkflowDefinitionProps = {
  workflowDefinitions: Array<WorkflowDefinition>;
  objectDisplay?: string;
  onCancel: () => void;
  onSelect: (workflowDefinitionId: string) => void;
};

type FormData = {
  workflowDefinitionId: string;
};

const SelectWorkflowDefinition = ({
  workflowDefinitions,
  objectDisplay,
  onCancel,
  onSelect,
}: SelectWorkflowDefinitionProps) => {
  const { control } = useForm<FormData>();

  const workflowOptions = workflowDefinitions.map(def => ({
    id: def.id,
    value: def.id,
    title: def.name,
  }));

  return (
    <CommonModal
      title="New workflow"
      onCancel={onCancel}
      subtitle={objectDisplay}
      content={
        <Controller
          name="workflowDefinitionId"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange } }) => (
            <SelectionCard.Group
              handleSelection={(value: string) => {
                onChange(value);
                onSelect(value);
              }}
              direction="vertical"
              spacing="compact"
            >
              <Body size="small" type="emphasis">
                WORKFLOWS
              </Body>
              {workflowOptions.map(option => (
                <SelectionCard.Item
                  key={option.id}
                  value={option.id}
                  borderStyle="ghost"
                  size="compact"
                  data-testid={`workflow-option-${option.id}`}
                >
                  <SelectionCard.CommonContent
                    iconName="folder-closed"
                    title={option.title}
                  />
                </SelectionCard.Item>
              ))}
            </SelectionCard.Group>
          )}
        />
      }
    />
  );
};
